/**
 * AckDialog — sheet 33, the typed ERROR acknowledgment (mission 7 WP10 T055; FR-012, FR-028, NFR-007; TX.4,
 * A.5, ANA.2, ANA.4, ANA.6, T.2; style guide §7.13; squad U8, U9, R24; OR-10, OI-3; contracts/ui-api.md
 * "AckDialog", whose ten rules this follows).
 *
 * 1. A non-blocking Radix dialog titled `ackTitle` with the count in mono, the ERROR badge, `SeverityLegend`
 *    (ANA.4, attribution included) and `ANA4_LEGEND.ERROR` restating the definition (OR-10).
 * 2. `ackErrorRulesHeading`, then the ERROR rules from the by-rule read, walked at most five pages and
 *    aggregated by `rule_id` (U9). No count is shown when `severity_counts_exact` is false, since each
 *    rule's figure is a sum over its segments. If a cursor remains, a link to Results says
 *    `ackMoreRulesUncounted`. mission 8 WP09 T048 (P5; FR-022 residual): the link is bound to the walk's own
 *    pages, which carry no per-rule totals for the rules they did not reach, so it names no number and
 *    makes no `rule_counts` or catalog read (mission 7's `ackMoreRules` count read both, and was wrong
 *    under a severity override). The list scrolls inside the modal.
 * 3. Explain toggles `RuleExplanation` inline under its rule (33/2's deviation); the typed text stays.
 * 4. `correctedUpload` links to New run (33/4). Beside it, `assistantAskAboutFindings` opens the run's
 *    page with the panel open and the typed prefill `{kind: 'findings', count}`, carried only by
 *    `assistantNavState` in router state, never in the URL (mission 8 WP09 T048; FR-013; SAT.1; R14, S19).
 * 5. `errorAcknowledgmentSentence(count)` in a `navy-50` mono block, then `typeSentenceExactly`.
 * 6. The input: `ackInputLabel`, `autocomplete="off"`, `spellcheck={false}`, described by the sentence and
 *    the helper. Paste is allowed (OI-3; on Legal's Q14 list). It has the initial focus.
 * 7. The match is exact (`===`, no trim, no case fold); until then the danger `transmitAnyway` is disabled
 *    with `ackDisabledReason`.
 * 8. `ackWhoWhenWhat`: the tenant's contact email, the current time in UTC and the selected bureaus (A.5).
 * 9. Ghost `modalCancel`, Esc or a press outside cancels: nothing is sent or recorded, and focus returns to
 *    the page's Transmit button (`returnFocus`; the page opens the dialog without a `DialogTrigger`).
 * 10. A 409 `error-acknowledgment-required` keeps the modal open with the served count, clears the typed
 *    text and announces `ackCountChanged`; the rule list is walked again, from the API rather than the
 *    cache (mission 8 WP09 T048; FR-022 residual). A served `ack_wording_version` other than `ERROR_ACK.version`
 *    (a deploy skew between Pages and the API) would be refused for ever, so the dialog asks for a reload,
 *    disables the input and the danger button, and sends nothing more (R24).
 *
 * "Transmit anyway" posts the bureaus, the count and `ERROR_ACK.version`, never the typed sentence (TX.4).
 * Any other refusal goes to the page (`onProblem`), which renders its band; a transport failure keeps the
 * modal and the typed text, with the generic band.
 */
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState, type RefObject } from 'react'
import { Link } from 'react-router-dom'

import { Band } from '@/components/trellis/Band'
import { RuleExplanation } from '@/components/trellis/RuleExplanation'
import { SeverityBadge } from '@/components/trellis/SeverityBadge'
import { SeverityLegend } from '@/components/trellis/SeverityLegend'
import { Button } from '@/components/ui/button'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiError, type Problem } from '@/lib/api'
import type { Bureau } from '@/lib/api/bureaus'
import type { ByRuleFilters, ByRulePage } from '@/lib/api/findings'
import { transmit, type TransmitAccepted } from '@/lib/api/transmissions'
import { assistantHref, assistantNavState } from '@/lib/assistant-panel'
import { Mono, count as formatCount, date, isoDateTime, time } from '@/lib/format'
import { ANA4_LEGEND, ERROR_ACK, errorAcknowledgmentSentence } from '@/lib/mandatory-copy'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { byRuleQuery } from '@/lib/queries'

/** The bounded walk: at most five by-rule pages, 250 rows (U9). */
export const ACK_MAX_PAGES = 5

const TX4 = 'error-acknowledgment-required'
const LINK = 'text-[var(--color-link)] underline hover:text-[var(--color-link-hover)]'

export interface AckDialogProps {
  open: boolean
  runId: string
  /** The run's ERROR count, or the count a TX.4 refusal served. */
  count: number
  /** The wording version a TX.4 refusal served; `ERROR_ACK.version` otherwise. */
  servedVersion?: string
  /** The selected bureaus, in canonical order. */
  bureaus: readonly Bureau[]
  /** The tenant's contact email (one identity; A.5 as amended). */
  email: string
  /** The page's Transmit button, focused again on cancel. */
  returnFocus: RefObject<HTMLElement | null>
  onCancel(): void
  onAccepted(body: TransmitAccepted): void
  onProblem(problem: Problem): void
}

interface AckRule {
  rule_id: string
  description: string | null
  count: number
}

type Walk = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; rules: AckRule[]; exact: boolean; more: boolean }

function aggregate(pages: readonly ByRulePage[]): AckRule[] {
  const byId = new Map<string, AckRule>()
  for (const p of pages) {
    for (const e of p.entries) {
      const seen = byId.get(e.rule_id)
      if (seen) seen.count += e.count
      else byId.set(e.rule_id, { rule_id: e.rule_id, description: e.description, count: e.count })
    }
  }
  return [...byId.values()]
}

/** `text` with the first occurrence of `part` in mono. */
function withMono(text: string, part: string) {
  const at = text.indexOf(part)
  if (at < 0) return text
  return (
    <>
      {text.slice(0, at)}
      <Mono>{part}</Mono>
      {text.slice(at + part.length)}
    </>
  )
}

/** The bounded walk; each `generation` after the first re-reads every page from the API (a 409 changed the count). */
function useRuleWalk(runId: string, generation: number): Walk {
  const qc = useQueryClient()
  const [walk, setWalk] = useState<Walk>({ kind: 'loading' })
  useEffect(() => {
    let live = true
    const fresh = generation > 0 ? { staleTime: 0 } : {}
    async function run() {
      const pages: ByRulePage[] = []
      let cursor: string | undefined
      for (let i = 0; i < ACK_MAX_PAGES; i++) {
        const filters: ByRuleFilters = cursor === undefined ? { severity: 'ERROR' } : { severity: 'ERROR', cursor }
        const body = await qc.fetchQuery({ ...byRuleQuery(runId, filters, true), retry: false, ...fresh })
        pages.push(body)
        cursor = body.next_cursor
        if (cursor === undefined) break
      }
      return { kind: 'ready', rules: aggregate(pages), exact: pages.every((p) => p.severity_counts_exact), more: cursor !== undefined } as const
    }
    run().then(
      (result) => live && setWalk(result),
      () => live && setWalk({ kind: 'error' }),
    )
    return () => {
      live = false
    }
  }, [qc, runId, generation])
  return walk
}

function RuleList({ runId, walk }: { runId: string; walk: Walk }) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  if (walk.kind === 'loading') return <p className="text-body-sm text-navy-600">{copyText('loading')}</p>
  if (walk.kind === 'error') {
    return (
      <Band kind="blocked">
        <p>{copyText('genericError')}</p>
      </Band>
    )
  }
  function toggle(id: string) {
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  return (
    <>
      <ul data-ack-rules="" className="max-h-[240px] overflow-y-auto rounded-md border border-navy-200">
        {walk.rules.map((r) => {
          const shown = open.has(r.rule_id)
          return (
            <li key={r.rule_id} data-ack-rule={r.rule_id} className="border-b border-navy-200 px-3 py-2 last:border-0">
              <div className="flex items-baseline gap-3">
                <Mono className="text-body-sm text-navy-950">{r.rule_id}</Mono>
                <span className="min-w-0 flex-1 text-body-sm text-navy-700">{r.description}</span>
                {walk.exact ? (
                  <Mono data-rule-count="" className="text-body-sm text-navy-950">
                    {formatCount(r.count)}
                  </Mono>
                ) : null}
                <Button variant="link" size="sm" aria-expanded={shown} onClick={() => toggle(r.rule_id)}>
                  {copyText(shown ? 'hideExplanation' : 'explainRule')}
                </Button>
              </div>
              {shown ? (
                <div className="mt-3">
                  <RuleExplanation ruleId={r.rule_id} />
                </div>
              ) : null}
            </li>
          )
        })}
      </ul>
      {/* mission 7 WP14: J6, whenever the walk has more; P5: from the walk's own pages, so no count. */}
      {walk.more ? (
        <p>
          <Link to={`/app/runs/${encodeURIComponent(runId)}?severity=ERROR`} className={`text-body-sm ${LINK}`}>
            {copyText('ackMoreRulesUncounted')}
          </Link>
        </p>
      ) : null}
    </>
  )
}

interface BodyProps extends Omit<AckDialogProps, 'open' | 'returnFocus'> {
  inputRef: RefObject<HTMLInputElement>
  /** The ANA.4 definition's id: the dialog's accessible description. */
  definitionId: string
}

function AckBody({ runId, count: initialCount, servedVersion, bureaus, email, inputRef, definitionId, onCancel, onAccepted, onProblem }: BodyProps) {
  const [count, setCount] = useState(initialCount)
  const [version, setVersion] = useState(servedVersion ?? ERROR_ACK.version)
  const [typed, setTyped] = useState('')
  const [announce, setAnnounce] = useState('')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const inFlight = useRef(false)
  const [generation, setGeneration] = useState(0)
  const walk = useRuleWalk(runId, generation)
  const sentenceId = useId()
  const helperId = useId()
  const inputId = useId()

  const sentence = errorAcknowledgmentSentence(count)
  const skewed = version !== ERROR_ACK.version
  const blocked = skewed || typed !== sentence
  const now = new Date().toISOString()
  const at = `${date(now)} ${time(now)}`
  const who = PROVISIONAL_COPY.ackWhoWhenWhat.render({ email, time: at, bureaus })
  const whoAt = who.indexOf(at)

  async function send() {
    if (inFlight.current || blocked) return
    inFlight.current = true
    setBusy(true)
    setFailed(false)
    try {
      // TX.4: the count and the wording version travel; the typed sentence never leaves the page.
      const body = await transmit(runId, { bureaus: [...bureaus], acknowledged_error_count: count, ack_wording_version: ERROR_ACK.version })
      onAccepted(body)
    } catch (err) {
      if (err instanceof ApiError && err.problem.type.endsWith(`/${TX4}`)) {
        const served = err.problem
        if (typeof served.error_count === 'number') setCount(served.error_count)
        if (typeof served.ack_wording_version === 'string') setVersion(served.ack_wording_version)
        setTyped('')
        setAnnounce(copyText('ackCountChanged'))
        // FR-022 residual: the count changed, so the rule list is read again.
        setGeneration((g) => g + 1)
      } else if (err instanceof ApiError) {
        onProblem(err.problem)
      } else {
        setFailed(true)
      }
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{withMono(PROVISIONAL_COPY.ackTitle.render({ count }), formatCount(count))}</DialogTitle>
        <SeverityBadge severity="ERROR" />
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        <SeverityLegend />
        <p className="flex items-baseline gap-2 text-body text-navy-950">
          <SeverityBadge severity="ERROR" />
          <span data-ana4-error="" id={definitionId}>
            {ANA4_LEGEND.ERROR}
          </span>
        </p>
        <section className="flex flex-col gap-2">
          <h3 className="text-h3 text-navy-950">{copyText('ackErrorRulesHeading')}</h3>
          <RuleList runId={runId} walk={walk} />
        </section>
        <p className="flex flex-wrap gap-x-4 gap-y-1 text-body-sm">
          <Link to="/app/runs/new" className={LINK}>
            {copyText('correctedUpload')}
          </Link>
          {/* 33/4 (SAT.1): the prefill travels in router state only (R14). */}
          <Link to={assistantHref(runId)} state={assistantNavState({ kind: 'findings', count })} className={LINK}>
            {copyText('assistantAskAboutFindings')}
          </Link>
        </p>
        {skewed ? (
          <Band kind="blocked">
            <p>{copyText('reloadAndRetry')}</p>
          </Band>
        ) : null}
        {failed ? (
          <Band kind="blocked">
            <p>{copyText('genericError')}</p>
          </Band>
        ) : null}
        <p id={sentenceId} className="rounded-md bg-navy-50 px-3 py-2 font-mono text-body text-navy-950">
          {sentence}
        </p>
        <p id={helperId} className="text-body-sm text-navy-600">
          {copyText('typeSentenceExactly')}
        </p>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={inputId}>{copyText('ackInputLabel')}</Label>
          <Input
            ref={inputRef}
            id={inputId}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            disabled={skewed}
            aria-describedby={`${sentenceId} ${helperId}`}
          />
        </div>
        <p data-ack-announce="" aria-live="polite" className="text-body-sm text-navy-700">
          {announce}
        </p>
        <p data-ack-who="" className="text-body-sm text-navy-700">
          {who.slice(0, whoAt)}
          <time dateTime={isoDateTime(now)}>{at}</time>
          {who.slice(whoAt + at.length)}
        </p>
        <p className="text-caption text-navy-600">{copyText('timesInUtc')}</p>
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>
          {copyText('modalCancel')}
        </Button>
        <Button
          variant="destructive"
          loading={busy}
          onClick={() => void send()}
          {...(blocked ? { disabled: true as const, disabledReason: copyText(skewed ? 'reloadAndRetry' : 'ackDisabledReason') } : {})}
        >
          {copyText('transmitAnyway')}
        </Button>
      </DialogFooter>
    </>
  )
}

export function AckDialog({ open, returnFocus, onCancel, onAccepted, ...rest }: AckDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const succeeded = useRef(false)
  const describedBy = useId()
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onCancel())}>
      <DialogContent
        size="lg"
        aria-describedby={describedBy}
        onOpenAutoFocus={(event) => {
          // Rule 6: the input takes the initial focus.
          event.preventDefault()
          succeeded.current = false
          inputRef.current?.focus()
        }}
        onCloseAutoFocus={(event) => {
          // Rule 9: back to the Transmit button; after a success the progress heading takes focus instead (U8).
          event.preventDefault()
          if (!succeeded.current) returnFocus.current?.focus()
        }}
      >
        <AckBody
          {...rest}
          inputRef={inputRef}
          definitionId={describedBy}
          onCancel={onCancel}
          onAccepted={(body) => {
            succeeded.current = true
            onAccepted(body)
          }}
        />
      </DialogContent>
    </Dialog>
  )
}
